<template>
    <div class="block-editor send-email">
        <div class="wrapper">
            <div class="title">{{ $t("mail.sendMailBlock.from") }}</div>
            <div class="text-editor-wrapper">
                <a-select v-model="clone.from" :defaultValue="null">
                    <a-select-option :selected="true" value="null">{{ $t("mail.sendMailBlock.selectEmailSender") }}</a-select-option>
                    <a-select-option
                        :key="senderMailAddress.mail"
                        v-for="senderMailAddress of senderMailAddresses"
                        :value="senderMailAddress.mail"
                    >
                        {{ senderMailAddress.mail }}
                    </a-select-option>
                </a-select>
            </div>
        </div>
        <div class="wrapper">
            <div class="title">{{ $t("mail.sendMailBlock.senderName") }}</div>
            <div class="text-editor-wrapper">
                <TiptapEditor
                    :placeholder="$t('mail.sendMailBlock.placeholderSenderName')"
                    :show-attributes="true"
                    :show-emoji="false"
                    v-model="clone.senderName"
                    automation-mode
                />
            </div>
        </div>
        <div class="wrapper">
            <div class="title">{{ $t("mail.sendMailBlock.to") }}</div>
            <div class="text-editor-wrapper">
                <TiptapEditor
                    :placeholder="$t('mail.sendMailBlock.placeholderRecipient')"
                    :show-attributes="true"
                    :show-emoji="false"
                    v-model="clone.to"
                    automation-mode
                />
            </div>
        </div>
        <div class="wrapper">
            <div class="title">{{ $t("mail.sendMailBlock.mailContent") }}</div>
            <div class="text-editor-wrapper">
                <a-select v-model="clone.type" :defaultValue="null">
                    <a-select-option value="custom">{{ $t("mail.sendMailBlock.typeCustom") }}</a-select-option>
                    <a-select-option value="template">{{ $t("mail.sendMailBlock.typeTemplate") }}</a-select-option>
                </a-select>
            </div>
        </div>
        <template v-if="clone.type === 'template'">
            <div class="wrapper">
                <div class="title">{{ $t("mail.sendMailBlock.mailTemplate") }}</div>
                <div class="text-editor-wrapper">
                    <a-select v-model="clone.templateId" :defaultValue="null">
                        <a-select-option :selected="true" value="null">{{ $t("mail.sendMailBlock.selectEmailTemplate") }}</a-select-option>
                        <a-select-option :key="template.id" v-for="template of templates" :value="template._id">
                            {{ template.name }}
                        </a-select-option>
                    </a-select>
                </div>
            </div>
            <template v-if="clone.templateId && clone.templateId !== 'null' && templateParamsForUi.length">
                <div
                    v-for="(row, idx) in templateParamsForUi"
                    :key="'tpl-param-' + row.param + '-' + idx"
                    class="wrapper template-param-row"
                >
                    <div class="title">{{ $t("mail.sendMailBlock.templateParamTitle", { param: row.param }) }}</div>
                    <div class="text-editor-wrapper">
                        <TiptapEditor
                            :placeholder="$t('mail.sendMailBlock.placeholderTemplateParam')"
                            :show-attributes="true"
                            :show-emoji="false"
                            v-model="row.value"
                            automation-mode
                        />
                    </div>
                </div>
            </template>
        </template>
        <template v-if="clone.type === 'custom'">
            <div class="wrapper">
                <div class="title">{{ $t("mail.sendMailBlock.subject") }}</div>
                <div class="text-editor-wrapper">
                    <input
                        type="text"
                        :placeholder="$t('mail.sendMailBlock.placeholderSubject')"
                        v-model="clone.subject"
                        style="border: none; padding: 5px"
                    />
                </div>
            </div>
            <div class="wrapper">
                <div class="title">{{ $t("mail.sendMailBlock.content") }}</div>
                <div class="text-editor-wrapper email">
                    <TiptapEditor
                        :placeholder="$t('mail.sendMailBlock.placeholderContentBody')"
                        :show-attributes="true"
                        :show-emoji="false"
                        v-model="clone.content"
                        automation-mode
                    />
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import _ from "lodash";

import { mapActions, mapState } from "vuex";
import TiptapEditor from "../../../../Chatbot/pages/dashboard/common/TiptapEditor.vue";
import log2 from "../../../../../../utils/log2";
import { MAIL_API } from "../../../../../../api/mail";

const log = log2("SendMail");
export default {
    name: "SendMail",
    components: { TiptapEditor },
    props: ["value"],
    data() {
        return {
            clone: _.cloneDeep(this.value),
            senderMailAddresses: [],
            defaultMail: "",
            templates: [],
        };
    },
    computed: {
        ...mapState({
            allMailAddresses: (state) => state.mail.allMailAddresses,
        }),
        templateParamsForUi() {
            return this.clone.templateParams || [];
        },
    },
    methods: {
        ...mapActions({}),
        extractParamNamesFromHtml(html) {
            if (!html) return [];
            const matches = html.match(/{{(.*?)}}/g) || [];
            const names = matches.map(function (m) {
                return m.replace(/{{|}}/g, "").trim();
            });
            return _.uniq(names);
        },
        async loadTemplateParamsForSelectedTemplate(templateId) {
            if (!templateId || templateId === "null") {
                this.$set(this.clone, "templateParams", []);
                return;
            }
            try {
                const tpl = await MAIL_API.TEMPLATE.getEmailTemplateById(templateId);
                const names = this.extractParamNamesFromHtml(tpl.html || "");
                const prev = this.clone.templateParams || [];
                const next = names.map(function (name) {
                    const found = _.find(prev, function (p) {
                        return p.param === name;
                    });
                    return { param: name, value: found ? found.value : "" };
                });
                this.$set(this.clone, "templateParams", next);
            } catch (e) {
                log("loadTemplateParamsForSelectedTemplate", e);
                this.$set(this.clone, "templateParams", []);
            }
        },
        async fetch() {
            const res = await MAIL_API.TEMPLATE.listEmailTemplates();
            this.templates = res.templates;
        },
    },
    mounted() {
        if (!this.clone.type) this.clone.type = "custom";
        this.senderMailAddresses = this.allMailAddresses.filter(
            (x) => !!x.isSender && (!!x.isSenderVerified || !!x.isVerified)
        );
        if (this.clone.type === "template" && this.clone.templateId && this.clone.templateId !== "null") {
            this.loadTemplateParamsForSelectedTemplate(this.clone.templateId);
        }
        //        this.senderMailAddresses = [{ mail: "info@supsis.com" }, ...this.senderMailAddresses];
    },
    watch: {
        clone: {
            immediate: true,
            deep: true,
            handler(newValue) {
                if (newValue.type === "template") this.fetch();
                this.$emit("input", newValue);
            },
        },
        "clone.templateId": function (id) {
            if (this.clone.type !== "template") return;
            this.loadTemplateParamsForSelectedTemplate(id);
        },
        "clone.type": function (t) {
            if (t !== "template") {
                this.$set(this.clone, "templateParams", []);
            } else if (this.clone.templateId && this.clone.templateId !== "null") {
                this.loadTemplateParamsForSelectedTemplate(this.clone.templateId);
            }
        },
    },
};
</script>

<style lang="scss">
.block-editor.send-email {
    display: flex;
    flex-direction: column;
    gap: 5px;

    .wrapper {
        padding: 10px;

        .title {
            font-weight: 600;
            font-size: 16px;
            line-height: 20px;
        }

        .text-editor-wrapper {
            border-radius: 0px;
            border: 1px solid #fff;
            background: white;

            &:hover {
                border: 1px solid #006cff !important;
            }

            &.email {
                .editable-editor-wrapper {
                    height: 200px !important;
                }
            }
        }

        .ant-select {
            width: 100%;
        }
    }
}
</style>
